import type { WorkflowDefinition } from "../types";

/**
 * W22 — Optimize an existing page for AI citation.
 *
 * The same tool-shaped playbook as W23, entered from the other end: W23 starts
 * from the search you want to win, this one starts from a page you already
 * published. So it is `perTarget` on the page's pipeline run — a content team
 * fixes several pages in the same week, and nobody should be blocked because a
 * colleague is mid-rewrite on another one.
 *
 * Every `since` is `scope: "target"`, which means they read the bound run's
 * chain and are false until the run is bound. Before binding, a colleague
 * scoring their own page must not check off this run's steps.
 */
export const optimizeContentWorkflow: WorkflowDefinition = {
	id: "optimize-content",
	wNumber: 22,
	title: "Optimize an existing page so AI cites it",
	outcome: "Fix a page you already have until AI reads it and quotes it.",
	audiences: ["content", "inhouse"],
	uses: ["AI Visibility Rankings", "Citation Optimizer", "Projects"],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "pipelineRun", label: "Page", pipelineMode: "page" },
	// Needs grounding searches to score a page against: BRAND and PRODUCT have
	// them, a PERSON's identity prompts emit none.
	isApplicable: (mode) => mode.showGroundingSearches,
	articleSlug: "how-to-optimize-existing-content-for-ai-citation",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "pick-page",
			title: "Pick the page with the biggest upside",
			page: "AI Visibility Rankings",
			route: "/ai-visibility-rankings",
			criterion: "You've picked the page you are going to fix.",
			why: "A page that already covers the topic and sits just off the rank is the fastest win. A topic you have never written about is a different job.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"Sort the searches by impact and see who holds the rank",
				"Find one you already have a page for",
				"Leave topics with no page at all for the gaps list",
			],
			enrichment: "seoGaps",
			// Judgement call: nothing watches "you decided which page to fix".
			complete: { kind: "manual" },
		},
		{
			id: "score",
			title: "Score the page against that search",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored a page for this workflow.",
			why: "The score tells you where the page falls out, check by check. Guessing which edit to make wastes a whole revision round.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Paste the page URL and pick the search it should win",
				"Read the stoplight strip, not the single number",
				"Open the failing checks for specific advice",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCreated",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.page", gte: 1 },
				],
			},
			signal: ({ state }) => {
				const score =
					state.target?.pipeline?.latestScore ??
					state.property?.pipeline.latestScore ??
					null;
				if (score === null) {
					return null;
				}
				return {
					kind: "score",
					label: "Citation score",
					value: score,
					target: 70,
				};
			},
		},
		{
			id: "revise",
			title: "Rewrite the sections that failed",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've generated a rewrite for this workflow.",
			why: "Fix the checks that failed and leave the rest of the page alone. A full rewrite costs more time and can break what already worked.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Generate the rewrite and read the diff check by check",
				"Keep your own voice wherever a check already passes",
				"Export the copy plus the title, meta and structured data",
			],
			complete: {
				kind: "since",
				event: "pipelineRevisionCreated",
				scope: "target",
			},
			spend: { kind: "citationRewrite" },
			signal: ({ state }) => {
				const revisions = state.target?.pipeline?.revisionCount ?? null;
				if (revisions === null) {
					return null;
				}
				return {
					kind: "count",
					label: "Rewrites generated",
					value: revisions,
					target: 1,
				};
			},
		},
		{
			id: "rescore",
			title: "Score it again before you publish",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion: "You've scored the page again after a rewrite.",
			why: "Scoring again is free, so never publish on faith. Two rounds usually clear it. If it still falls short, the page needs more than an edit.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Score the revision and compare it with the first number",
				"Look at the checks you set out to fix, one by one",
				"Publish through your CMS once the banner clears",
			],
			complete: {
				kind: "all",
				of: [
					{
						kind: "since",
						event: "pipelineRunCompleted",
						scope: "target",
					},
					{ kind: "count", key: "pipeline.rescored", gte: 1 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Scores after a rewrite",
							value: state.property.pipeline.rescored,
							target: 1,
						}
					: null,
		},
		{
			id: "measure",
			title: "Measure the page in an SEO project",
			page: "Projects",
			route: "/projects",
			criterion: "You've got an SEO project tracking the page.",
			why: "The project is what puts the change on the record. Six weeks later nobody remembers what the rank was before you touched it.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Add the page to a page group on an SEO project",
				"Note the publish date so the climb has a start",
				"Watch the rank over the next two weeks",
			],
			// An existing SEO project is the right home for the page, so this
			// one step is allowed to start ticked. A fresh project per page
			// would scatter the measurement it exists to collect.
			complete: { kind: "count", key: "projectTypes.SEO", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "SEO projects",
							value: state.property.projectTypes.SEO,
							target: 1,
						}
					: null,
		},
	],
};
